'use client';

import { useRouter } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { useQueryState } from 'nuqs';
import { omitBy, truncate } from 'lodash-es';

import { SuggestedKeywordList, SearchForm, SearchHistory, Exhibition } from '~/app/search/_components';
import { suggestedKeywordsOptions, keywordsRankingsOptions, bannersOptions } from '~/app/search/_queries';
import { AppBar, LogVisit, RankedKeywords, Spacing } from '~/components';
import { useEnvironmentInfo, useSearchHistory, useSearchLog } from '~/hooks';
import { CONTENT_TYPE, EVENT_ACTION, EVENT_TYPE, PAGE_ID } from '~/apis/log/constant';
import { SuggestedCategory } from '~/apis/search';
import { isEmptyText } from '~/utils';
import { goToFullWebviewOnApp } from '~/services/app-interface';
import { KeywordsRanking } from '~/apis/mercari/get-keywords-rankings';
import { ShopEntryPoints } from '~/app/_components';

const DEFAULT_SEARH_LOG_PARAMS = {
  event_type: EVENT_TYPE.SEARCH,
  event_action: EVENT_ACTION.SEARCH,
  page_id: PAGE_ID.SEARCH,
};
export const Search = () => {
  const router = useRouter();
  const { isWebview } = useEnvironmentInfo();
  const [q, setQ] = useQueryState('q');
  const { sendSearchLog } = useSearchLog();

  const { data: suggestedKeywordsData } = useQuery(
    suggestedKeywordsOptions(
      omitBy({ q }, (value) => value === ''),
      { enabled: Boolean(q) },
    ),
  );
  const { data: rankedKeywordsData } = useQuery(keywordsRankingsOptions({ size: 10 }));
  const { data: bannerData } = useQuery(bannersOptions({ device: 'm', inventoryId: 'mercari_search_bottom' }));
  const { searchHistories, addSearchHistory, removeSearchHistory, clearSearchHistory } = useSearchHistory();

  const handleSearchInputChange = (value: string) => {
    if (isEmptyText(value)) return;
    setQ(value);
  };

  const navigateToSearchResult = (value: string, categoryId?: string) => {
    const truncatedValue = truncate(value, {
      length: 40,
    });
    if (isEmptyText(truncatedValue)) return;
    addSearchHistory(truncatedValue);
    router.push(`/search/result?q=${truncatedValue}${categoryId ? `&categoryId=${categoryId}` : ''}`);
  };

  const handleSearchHistoryKeywordClick = (value: string) => {
    sendSearchLog({
      ...DEFAULT_SEARH_LOG_PARAMS,
      search_term: value,
      ref_content: '최근검색어',
    });
    navigateToSearchResult(value);
  };

  const handleRankedKeywordClick = (value: KeywordsRanking) => {
    sendSearchLog({
      ...DEFAULT_SEARH_LOG_PARAMS,
      search_term: value.keyword,
      ref_content: '인기검색어',
    });
    navigateToSearchResult(value.keyword);
  };
  const handleSearchFormSubmit = (value: string) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return;
    sendSearchLog({
      ...DEFAULT_SEARH_LOG_PARAMS,
      search_term: trimmedValue,
    });
    navigateToSearchResult(trimmedValue);
  };

  const handleSuggestKeywordClick = (value: string, index: number) => {
    sendSearchLog({
      ...DEFAULT_SEARH_LOG_PARAMS,
      search_term: value,
      ref_term: q as string,
      ref_content: '자동완성',
      content_position: index,
      content_type: CONTENT_TYPE.MERCARI,
    });
    navigateToSearchResult(value);
  };

  const handleSuggestCategoryKeywordClick = (category: SuggestedCategory, index: number) => {
    sendSearchLog({
      ...DEFAULT_SEARH_LOG_PARAMS,
      ref_term: q as string,
      ref_content: '자동완성',
      content_id: category.categoryId,
      content_position: index,
      content_type: CONTENT_TYPE.CATEGORY,
    });
    navigateToSearchResult(category.keyword, category.categoryId);
  };

  const handleExhibitionClick = (webUrl: string) => {
    if (isWebview) {
      goToFullWebviewOnApp({ url: webUrl }).go();
      return;
    }
    globalThis.window.open(webUrl, '_blank');
  };

  return (
    <>
      <LogVisit
        params={{
          event_type: EVENT_TYPE.VISIT,
          event_action: EVENT_ACTION.VISIT,
          page_id: PAGE_ID.SEARCH,
        }}
      />
      <AppBar
        slot={
          <SearchForm value={q ?? ''} onChange={handleSearchInputChange} onSubmit={handleSearchFormSubmit} allowClear />
        }
      />

      {q ? (
        <>
          <Spacing height={30} />
          <SuggestedKeywordList
            q={q}
            suggestedKeywords={suggestedKeywordsData}
            onKeywordClick={handleSuggestKeywordClick}
            onCategoryClick={handleSuggestCategoryKeywordClick}
          />
        </>
      ) : (
        <>
          <Spacing height={20} />
          {searchHistories?.length > 0 && (
            <>
              <SearchHistory
                histories={searchHistories}
                onKeywordClick={handleSearchHistoryKeywordClick}
                onKeywordDelete={removeSearchHistory}
                onHistoryClear={clearSearchHistory}
              />
              <Spacing height={40} />
            </>
          )}
          {rankedKeywordsData && (
            <>
              <RankedKeywords
                title={'실시간 인기 검색어'}
                rankedKeywords={rankedKeywordsData}
                onKeywordClick={handleRankedKeywordClick}
              />
              <Spacing height={40} />
            </>
          )}
          <ShopEntryPoints />
          <Spacing height={40} />
          <Exhibition banners={bannerData} onClick={handleExhibitionClick} />
        </>
      )}
    </>
  );
};
